import { useEffect, useRef, useState, type ChangeEvent, type CSSProperties } from 'react';
import { api, resolveAssetUrl, type AdminCosmetic, type Rarity } from '@/api/client';
import { ErrorBox, Field, Loading, Modal, PageTitle } from '@/components/ui';

const DEFAULT_GRADIENT =
  'radial-gradient(circle at 50% 25%, rgba(34,211,238,.3), transparent 35%), linear-gradient(180deg, #1E1B4B, #050314)';
const DEFAULT_LANES = ['#312e81', '#24205f', '#17143f', '#0b0925'];
const DEFAULT_WALL = ['#5a5358', '#3d3739', '#2a2528', '#1c1a1d'];
const PLAY_COLORS = ['#E63946', '#3B82F6', '#F4C724', '#9B5DE5'];

// Seeded backgrounds live with the game client. Bundle them into Admin as
// hashed Vite assets so `/assets/match-backgrounds/...` previews work in local,
// staging and production without depending on the game client origin.
const bundledBackgroundModules = import.meta.glob(
  '../../../client/public/assets/match-backgrounds/*.{jpg,jpeg,png,webp}',
  { eager: true, query: '?url', import: 'default' },
) as Record<string, string>;

const BUNDLED_BACKGROUNDS = new Map(
  Object.entries(bundledBackgroundModules).map(([path, url]) => [path.split('/').pop() ?? path, url]),
);

function imageUrl(item: AdminCosmetic | null): string {
  return (item?.data?.imageUrl as string | undefined) ?? item?.iconUrl ?? '';
}

function laneColors(item: AdminCosmetic | null): string[] {
  const value = item?.data?.laneColors;
  return Array.isArray(value) && value.length >= 4
    ? value.slice(0, 4).map(String)
    : DEFAULT_LANES;
}

function resolveBackgroundImage(image: string): string {
  if (!image) return '';
  const match = image.match(/\/assets\/match-backgrounds\/([^?#/]+)(?:[?#].*)?$/);
  const bundled = match ? BUNDLED_BACKGROUNDS.get(match[1]) : null;
  return bundled ?? resolveAssetUrl(image) ?? '';
}

function BackgroundPreview({
  background,
  image,
  accent,
  height = 130,
}: {
  background: string;
  image: string;
  accent: string;
  height?: number;
}) {
  const resolved = resolveBackgroundImage(image);
  return (
    <div
      style={{
        width: '100%',
        height,
        position: 'relative',
        overflow: 'hidden',
        borderRadius: 12,
        border: `1px solid ${accent}88`,
        background: resolved
          ? `linear-gradient(rgba(5,3,20,.25), rgba(5,3,20,.62)), url("${resolved}") center / cover no-repeat`
          : background || DEFAULT_GRADIENT,
        boxShadow: `inset 0 0 30px rgba(0,0,0,.3), 0 0 16px ${accent}22`,
      }}
    >
      <div
        style={{
          position: 'absolute',
          inset: '10px 18% 8px',
          border: '1px solid rgba(255,255,255,.13)',
          borderRadius: 8,
          background: 'rgba(5,3,20,.25)',
        }}
      />
      <div
        style={{
          position: 'absolute',
          left: '50%',
          top: 12,
          bottom: 10,
          width: 1,
          background: `${accent}88`,
        }}
      />
      {['#E63946', '#3B82F6', '#F4C724', '#9B5DE5'].map((color, index) => (
        <span
          key={color}
          style={{
            position: 'absolute',
            left: `${29 + index * 14}%`,
            bottom: 22 + (index % 2) * 22,
            width: 12,
            height: 12,
            borderRadius: '50%',
            background: color,
            boxShadow: `0 0 10px ${color}`,
          }}
        />
      ))}
    </div>
  );
}

function arenaBackground(background: string, image: string, shade = true): string {
  const resolved = resolveBackgroundImage(image);
  if (!resolved) return background || DEFAULT_GRADIENT;
  return `${shade ? 'linear-gradient(rgba(5,3,20,.16), rgba(5,3,20,.5)), ' : ''}url("${resolved}") center / cover no-repeat`;
}

function PreviewLane({
  selected,
  side,
  background,
  image,
  lanes,
  laneOpacity,
}: {
  selected: boolean;
  side: 'left' | 'right';
  background: string;
  image: string;
  lanes: string[];
  laneOpacity: number;
}) {
  const bands = selected ? lanes : DEFAULT_WALL;
  const opacity = selected ? Math.min(0.32, laneOpacity) : 1;
  const holds = [
    { top: '20%', left: '66%', color: PLAY_COLORS[0] },
    { top: '34%', left: '38%', color: PLAY_COLORS[1] },
    { top: '49%', left: '62%', color: PLAY_COLORS[2] },
    { top: '64%', left: '42%', color: PLAY_COLORS[3] },
    { top: '78%', left: '56%', color: PLAY_COLORS[0] },
  ];

  return (
    <div
      style={{
        position: 'relative',
        overflow: 'hidden',
        minWidth: 0,
        borderRadius: 4,
        background: selected
          ? arenaBackground(background, image, false)
          : 'linear-gradient(180deg, #5a5358 0 35%, #3d3739 35% 70%, #2a2528 70% 90%, #1c1a1d 90%)',
        boxShadow: 'inset 0 0 0 1px rgba(0,0,0,.55)',
      }}
    >
      <div style={{ position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column' }}>
        {bands.map((color, index) => (
          <span
            key={`${color}-${index}`}
            style={{
              background: color,
              opacity,
              flex: index < 2 ? '0 0 35%' : index === 2 ? '0 0 20%' : '1 1 10%',
            }}
          />
        ))}
      </div>
      <div
        style={{
          position: 'absolute',
          inset: 0,
          background: 'linear-gradient(76deg, transparent 0 34%, rgba(0,0,0,.2) 35% 42%, transparent 43%), radial-gradient(ellipse at 50% 0%, rgba(255,255,255,.12), transparent 24%)',
        }}
      />
      <div
        style={{
          position: 'absolute',
          inset: '0 0 auto',
          height: 42,
          padding: '7px 8px',
          background: side === 'left' ? 'rgba(68,15,33,.82)' : 'rgba(12,39,74,.82)',
          borderBottom: `2px solid ${side === 'left' ? PLAY_COLORS[0] : PLAY_COLORS[1]}`,
          textAlign: side,
        }}
      >
        <div className="display" style={{ fontSize: 8, color: side === 'left' ? '#ff8790' : '#7ab7ff' }}>
          {side === 'left' ? 'YOU' : 'RIVAL'}
        </div>
        <div className="display" style={{ fontSize: 11 }}>CLIMBER</div>
      </div>
      <div style={{ position: 'absolute', top: 42, left: '50%', bottom: 18, width: 1, borderLeft: '1px dashed rgba(244,199,36,.35)' }} />
      {holds.map((hold, index) => (
        <span
          key={index}
          style={{
            position: 'absolute',
            top: hold.top,
            left: hold.left,
            width: 24,
            height: 8,
            transform: 'translate(-50%, -50%)',
            borderRadius: '55% 45% 52% 48%',
            background: hold.color,
            border: '1px solid rgba(255,255,255,.8)',
            boxShadow: `0 0 7px ${hold.color}`,
          }}
        />
      ))}
      <div
        style={{
          position: 'absolute',
          left: '50%',
          bottom: 24,
          width: 30,
          height: 38,
          transform: 'translateX(-50%)',
          borderRadius: '48% 48% 44% 44%',
          background: '#f8ffff',
          border: '2px solid #22D3EE',
          boxShadow: '0 0 12px rgba(34,211,238,.8)',
        }}
      >
        <span style={{ position: 'absolute', left: 5, top: 7, width: 4, height: 4, borderRadius: '50%', background: '#1E1B4B' }} />
        <span style={{ position: 'absolute', right: 5, top: 7, width: 4, height: 4, borderRadius: '50%', background: '#1E1B4B' }} />
      </div>
      <div
        className="display"
        style={{
          position: 'absolute',
          left: 0,
          right: 0,
          bottom: 7,
          height: 10,
          color: '#1E1B4B',
          fontSize: 7,
          lineHeight: '10px',
          textAlign: 'center',
          background: 'repeating-linear-gradient(135deg, #F4C724 0 7px, #1E1B4B 7px 12px)',
          textShadow: '0 0 2px white',
        }}
      >
        START
      </div>
    </div>
  );
}

function MatchScreenPreview({
  background,
  image,
  accent,
  lanes,
  laneOpacity,
}: {
  background: string;
  image: string;
  accent: string;
  lanes: string[];
  laneOpacity: number;
}) {
  const [perspective, setPerspective] = useState<'owner' | 'rival'>('owner');
  const selectedSide = perspective === 'owner' ? 'left' : 'right';
  const screenBackground = perspective === 'owner'
    ? arenaBackground(background, image)
    : DEFAULT_GRADIENT;
  const skillStyle: CSSProperties = {
    width: 36,
    height: 36,
    borderRadius: 9,
    border: `1px solid ${accent}`,
    display: 'grid',
    placeItems: 'center',
    fontSize: 17,
    background: 'rgba(5,3,20,.82)',
  };

  return (
    <div>
      <div style={{ display: 'flex', gap: 6, marginBottom: 8 }}>
        <button
          type="button"
          className={`btn ${perspective === 'owner' ? 'btn-primary' : ''}`}
          onClick={() => setPerspective('owner')}
          style={{ flex: 1, padding: '7px 8px' }}
        >
          OWNER VIEW
        </button>
        <button
          type="button"
          className={`btn ${perspective === 'rival' ? 'btn-primary' : ''}`}
          onClick={() => setPerspective('rival')}
          style={{ flex: 1, padding: '7px 8px' }}
        >
          RIVAL VIEW
        </button>
      </div>
      <div
        style={{
          width: 'min(100%, 330px)',
          aspectRatio: '9 / 18.5',
          margin: '0 auto',
          position: 'relative',
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
          borderRadius: 26,
          border: `2px solid ${accent}99`,
          background: screenBackground,
          boxShadow: `0 18px 50px rgba(0,0,0,.45), 0 0 22px ${accent}22`,
        }}
      >
        <div
          style={{
            height: 38,
            flexShrink: 0,
            display: 'grid',
            gridTemplateColumns: '42px 1fr 42px',
            alignItems: 'center',
            padding: '0 10px',
            background: 'rgba(4,2,14,.88)',
            borderBottom: '1px solid rgba(255,255,255,.08)',
          }}
        >
          <span className="display" style={{ color: '#ff6472', fontSize: 12 }}>×</span>
          <span className="display" style={{ color: '#22D3EE', textAlign: 'center', fontSize: 18 }}>12.8s</span>
          <span className="display" style={{ color: '#F4C724', textAlign: 'right', fontSize: 9 }}>x4</span>
        </div>
        <div style={{ flex: '1 1 auto', minHeight: 0, display: 'grid', gridTemplateColumns: '1fr 18px 1fr', gap: 3, padding: '5px 7px 0' }}>
          <PreviewLane
            selected={selectedSide === 'left'}
            side="left"
            background={background}
            image={image}
            lanes={lanes}
            laneOpacity={laneOpacity}
          />
          <div
            className="display"
            style={{
              writingMode: 'vertical-rl',
              color: '#ff5968',
              fontSize: 7,
              letterSpacing: '.16em',
              textAlign: 'center',
              paddingTop: 55,
              background: 'rgba(16,12,45,.92)',
              borderRadius: 4,
            }}
          >
            WR TRACK
          </div>
          <PreviewLane
            selected={selectedSide === 'right'}
            side="right"
            background={background}
            image={image}
            lanes={lanes}
            laneOpacity={laneOpacity}
          />
        </div>
        <div style={{ margin: '5px 9px 0', padding: '7px 8px', borderRadius: 7, color: 'rgba(245,243,255,.72)', background: 'rgba(5,3,20,.8)', fontSize: 8 }}>
          <b style={{ color: '#F4C724' }}>TIP</b> Accuracy beats speed — one wrong tap makes you fall.
        </div>
        <div style={{ height: 112, flexShrink: 0, display: 'grid', gridTemplateColumns: '46px 1fr 46px', alignItems: 'center', gap: 7, padding: '8px 10px 12px' }}>
          <div style={skillStyle}>🌀</div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 6, justifyItems: 'center' }}>
            {PLAY_COLORS.map((color) => (
              <span
                key={color}
                style={{
                  width: 39,
                  height: 39,
                  borderRadius: '50%',
                  background: `radial-gradient(circle at 38% 28%, #fff9, transparent 22%), ${color}`,
                  border: '3px solid white',
                  boxShadow: `0 4px 0 rgba(0,0,0,.45), 0 0 8px ${color}88`,
                }}
              />
            ))}
          </div>
          <div style={{ ...skillStyle, borderColor: '#22D3EE' }}>🛡️</div>
        </div>
        <div style={{ position: 'absolute', inset: 0, pointerEvents: 'none', background: 'repeating-linear-gradient(0deg, transparent 0 4px, rgba(255,255,255,.012) 4px 5px)' }} />
      </div>
      <div style={{ marginTop: 8, color: 'var(--ink-dim)', fontSize: 10, textAlign: 'center', lineHeight: 1.45 }}>
        {perspective === 'owner'
          ? 'Owner sees this arena across the screen and on the left lane.'
          : 'A rival without a background sees it only on the owner\'s right lane.'}
      </div>
    </div>
  );
}

export function BackgroundsPage() {
  const [items, setItems] = useState<AdminCosmetic[] | null>(null);
  const [editing, setEditing] = useState<AdminCosmetic | 'new' | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = (): void => {
    void api.cosmetics()
      .then(({ items: cosmetics }) => {
        setItems(cosmetics.filter((item) => item.slot === 'matchBackground'));
        setError(null);
      })
      .catch((reason) => setError(String(reason)));
  };

  useEffect(load, []);

  if (error && !items) return <ErrorBox text={error} />;
  if (!items) return <Loading />;

  return (
    <div>
      <PageTitle
        title="Match Backgrounds"
        action={(
          <button className="btn btn-primary" onClick={() => setEditing('new')}>
            + NEW BACKGROUND
          </button>
        )}
      />
      <div className="tag" style={{ marginBottom: 10 }}>
        PURCHASABLE PVP ARENA THEMES
      </div>
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fill, minmax(250px, 1fr))',
          gap: 12,
        }}
      >
        {items.map((item) => {
          const accent = (item.data?.accent as string | undefined) ?? '#22D3EE';
          return (
            <button
              key={item.id}
              className="card"
              onClick={() => setEditing(item)}
              style={{ padding: 10, color: 'inherit', textAlign: 'left', cursor: 'pointer' }}
            >
              <BackgroundPreview
                background={(item.data?.background as string | undefined) ?? DEFAULT_GRADIENT}
                image={imageUrl(item)}
                accent={accent}
              />
              <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8, marginTop: 9 }}>
                <strong>{item.name}</strong>
                <span className={`badge ${item.isActive ? 'badge-guest' : 'badge-ban'}`}>
                  {item.isActive ? 'SHOP' : 'HIDDEN'}
                </span>
              </div>
              <div className="mono" style={{ marginTop: 4, color: 'var(--ink-muted)', fontSize: 11 }}>
                {item.priceGems > 0 ? `${item.priceGems} gems` : `${item.priceCoins.toLocaleString()} coins`}
              </div>
            </button>
          );
        })}
      </div>

      {editing && (
        <BackgroundEdit
          initial={editing === 'new' ? null : editing}
          onClose={() => setEditing(null)}
          onDone={() => {
            setEditing(null);
            load();
          }}
        />
      )}
    </div>
  );
}

function BackgroundEdit({
  initial,
  onClose,
  onDone,
}: {
  initial: AdminCosmetic | null;
  onClose: () => void;
  onDone: () => void;
}) {
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [name, setName] = useState(initial?.name ?? '');
  const [description, setDescription] = useState(initial?.description ?? '');
  const [rarity, setRarity] = useState<Rarity>(initial?.rarity ?? 'rare');
  const [priceCoins, setPriceCoins] = useState(initial?.priceCoins ?? 0);
  const [priceGems, setPriceGems] = useState(initial?.priceGems ?? 0);
  const [background, setBackground] = useState(
    (initial?.data?.background as string | undefined) ?? DEFAULT_GRADIENT,
  );
  const [image, setImage] = useState(imageUrl(initial));
  const [accent, setAccent] = useState(
    (initial?.data?.accent as string | undefined) ?? '#22D3EE',
  );
  const [lanes, setLanes] = useState(laneColors(initial));
  const [laneOpacity, setLaneOpacity] = useState(
    Number(initial?.data?.laneOpacity ?? 0.82),
  );
  const [isActive, setIsActive] = useState(initial?.isActive ?? true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const save = async (): Promise<void> => {
    if (!name.trim()) {
      setError('Name is required');
      return;
    }
    setBusy(true);
    setError(null);
    const payload = {
      slot: 'matchBackground',
      name: name.trim(),
      description: description.trim() || null,
      rarity,
      priceCoins,
      priceGems,
      iconUrl: image || null,
      data: {
        ...(initial?.data ?? {}),
        background,
        imageUrl: image || null,
        accent,
        laneColors: lanes,
        laneOpacity: Math.min(1, Math.max(0.35, laneOpacity)),
      },
      isActive,
      isStarter: false,
    } as const;
    try {
      if (initial) await api.patchCosmetic(initial.id, payload);
      else await api.createCosmetic(payload);
      onDone();
    } catch (reason) {
      setError(String(reason));
    } finally {
      setBusy(false);
    }
  };

  const remove = async (): Promise<void> => {
    if (!initial || !confirm(`Delete background "${initial.name}"?`)) return;
    setBusy(true);
    try {
      await api.deleteCosmetic(initial.id);
      onDone();
    } catch (reason) {
      setError(String(reason));
      setBusy(false);
    }
  };

  const upload = async (event: ChangeEvent<HTMLInputElement>): Promise<void> => {
    const file = event.target.files?.[0];
    if (!file) return;
    setBusy(true);
    setError(null);
    try {
      const result = await api.uploadCosmeticIcon(file);
      setImage(result.url);
    } catch (reason) {
      setError(String(reason));
    } finally {
      setBusy(false);
      if (inputRef.current) inputRef.current.value = '';
    }
  };

  return (
    <Modal
      title={initial ? `Edit · ${initial.name}` : 'New match background'}
      onClose={onClose}
      maxWidth={960}
      footer={(
        <>
          {initial && <button className="btn btn-danger" onClick={() => void remove()} disabled={busy}>Delete</button>}
          <div style={{ flex: 1 }} />
          <button className="btn" onClick={onClose}>Cancel</button>
          <button className="btn btn-primary" onClick={() => void save()} disabled={busy}>
            {busy ? 'Saving…' : 'Save'}
          </button>
        </>
      )}
    >
      <div className="background-editor-grid">
        <div className="background-match-preview">
          <div className="tag" style={{ marginBottom: 8 }}>LIVE MATCH SCREEN PREVIEW</div>
          <MatchScreenPreview
            background={background}
            image={image}
            accent={accent}
            lanes={lanes}
            laneOpacity={laneOpacity}
          />
        </div>
        <div className="background-editor-fields">
          <BackgroundPreview background={background} image={image} accent={accent} height={120} />
          <Field label="NAME"><input value={name} onChange={(event) => setName(event.target.value)} /></Field>
          <Field label="DESCRIPTION"><input value={description} onChange={(event) => setDescription(event.target.value)} /></Field>
          <Field label="RARITY">
            <select value={rarity} onChange={(event) => setRarity(event.target.value as Rarity)}>
              <option value="common">common</option><option value="rare">rare</option>
              <option value="epic">epic</option><option value="legendary">legendary</option>
            </select>
          </Field>
          <Field label="BACKGROUND IMAGE" hint="Portrait image recommended (9:16). Uploaded through cosmetic storage.">
            <input ref={inputRef} type="file" accept="image/png,image/jpeg,image/webp" onChange={(event) => void upload(event)} />
            <input value={image} onChange={(event) => setImage(event.target.value)} placeholder="/uploads/cosmetics/..." />
          </Field>
          <Field label="CSS BACKGROUND" hint="Used when no image is uploaded.">
            <textarea rows={4} value={background} onChange={(event) => setBackground(event.target.value)} />
          </Field>
          <Field label="ACCENT"><input type="color" value={accent} onChange={(event) => setAccent(event.target.value)} /></Field>
          <Field label="PIXI LANE COLORS" hint="Top to bottom — keeps holds readable over the arena.">
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 7 }}>
              {lanes.map((color, index) => (
                <input
                  key={index}
                  type="color"
                  value={color}
                  onChange={(event) => setLanes((current) => current.map((value, itemIndex) => itemIndex === index ? event.target.value : value))}
                />
              ))}
            </div>
          </Field>
          <Field label={`LANE OPACITY · ${laneOpacity.toFixed(2)}`}>
            <input type="range" min="0.35" max="1" step="0.01" value={laneOpacity} onChange={(event) => setLaneOpacity(Number(event.target.value))} />
          </Field>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
            <Field label="PRICE (COINS)"><input type="number" min="0" value={priceCoins} onChange={(event) => setPriceCoins(Number(event.target.value) || 0)} /></Field>
            <Field label="PRICE (GEMS)"><input type="number" min="0" value={priceGems} onChange={(event) => setPriceGems(Number(event.target.value) || 0)} /></Field>
          </div>
          <label style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <input type="checkbox" checked={isActive} onChange={(event) => setIsActive(event.target.checked)} /> Active in shop
          </label>
        </div>
      </div>
      {error && <ErrorBox text={error} />}
    </Modal>
  );
}
